import React, { ReactNode, useCallback, useState } from 'react'
import { Box, Fade, Stack, Typography } from '@mui/material'

import { BEIGE_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'

import { PfChipStrip } from '../PfChipStrip'

export interface PfSubTabPage {
  id: string
  label: string
  /** An unvisited incomplete page reads as "not filled yet", not as an error. A complete page shows as such either way. */
  visited: boolean
  isValid: boolean
  content: ReactNode
}

export interface PfSubTabConfiguratorProps {
  title: string
  description: string
  pages: PfSubTabPage[]
  activeId: string
  onSelect: (id: string) => void
  /** Keep hidden pages mounted, for pages holding unsaved local state (row ids, mid-edit text, upload queues). */
  keepMounted?: boolean
}

/**
 * @component PfSubTabConfigurator
 * A configuration form split into pages behind a chip strip. A complete page is marked as such even on a later
 * visit to the step (`visited` lives in component state and restarts on remount); an incomplete one is flagged
 * only once visited.
 *
 * @example
 * <PfSubTabConfigurator title={t('…information_fill')} description={t('…information_fill_description')}
 *   pages={pages} activeId={tabs.active} onSelect={tabs.select} keepMounted />
 */
export const PfSubTabConfigurator: React.FC<PfSubTabConfiguratorProps> = ({ title, description, pages, activeId, onSelect, keepMounted = false }) => (
  <Fade in>
    <Stack gap="1.6rem" width="100%">

      <BorderBoxWrapper backgroundColor={BEIGE_500} padding="2.4rem" elevation="none" border="none">
        <Stack gap="1.6rem" alignItems="center" justifyContent="center">

          <Stack gap=".4rem" alignItems="center" textAlign="center">
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>{title}</Typography>
            <Typography variant="text-sm" fontWeight={400} color={GRAY_700}>{description}</Typography>
          </Stack>

          <Box width="100%">
            <PfChipStrip
              items={pages.map((page) => ({
                id: page.id,
                label: page.label,
                isComplete: false,
                userConfirmed: false,
                validity: page.isValid ? 'valid' : page.visited ? 'invalid' : 'untouched',
              }))}
              activeId={activeId}
              onSelect={onSelect}
              ariaLabel={title}
            />
          </Box>

        </Stack>
      </BorderBoxWrapper>

      {pages.map((page) => keepMounted
        ? <Box key={page.id} hidden={page.id !== activeId}>{page.content}</Box>
        : page.id === activeId && <React.Fragment key={page.id}>{page.content}</React.Fragment>
      )}

    </Stack>
  </Fade>
)

/** The active page and the pages visited so far, starting with the first one shown. */
export function useSubTabs<T extends string>(first: T) {
  const [active, setActive] = useState<T>(first)
  const [visited, setVisited] = useState<ReadonlySet<T>>(() => new Set([first]))

  const select = useCallback((id: string) => {
    const page = id as T
    setVisited((prev) => (prev.has(page) ? prev : new Set(prev).add(page)))
    setActive(page)
  }, [])

  return { active, visited, select }
}
